import { Box, Button, Fade, Popover, PopoverOrigin } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import useOverflowYItems from '@src/hooks/useOverflowYItems';
import { useEffect, useMemo, useRef, useState } from 'react';
import './tag-list_style.css';
import TagLabel from './TagLabel';
import { AnyTag } from './types';
import { positions } from './utils';

interface ITagsListView {
  tags: AnyTag[];
  heigthOfTagsListDiv?: number;
  position?: 'left' | 'under';
  inlineBtnPopup?: boolean;
  styleBtnPopup?: CSSProperties;
  handleOnDelete?: <T extends AnyTag>(
    tag: T,
    checked: boolean
  ) => void | Promise<void>;
  ellipsis?: boolean;
  columnView?: boolean;
  customAddButton?: React.ReactNode;
}

const DEFAULT_HEIGTH = 30;
const GAP_BETWEEN_TAGS = 8;

const RowTagsList: React.FC<ITagsListView> = ({
  tags,
  handleOnDelete,
  position = 'under',
  styleBtnPopup = {},
  heigthOfTagsListDiv,
  ellipsis,
  columnView,
  customAddButton,
}) => {
  const tagsListRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { totalVisibleItems, widthVisibleElements } = useOverflowYItems(
    tagsListRef,
    heigthOfTagsListDiv || DEFAULT_HEIGTH,
    GAP_BETWEEN_TAGS
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOverflow = totalVisibleItems < tags.length && tags.length > 0;

  const showMoreTagsPopup = (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      elevation={0}
      TransitionComponent={Fade}
      anchorOrigin={positions[position].anchorOrigin as PopoverOrigin}
      transformOrigin={positions[position].transformOrigin as PopoverOrigin}
      style={{ marginTop: position === 'under' ? 0 : '8px' }}
    >
      <Box
        minWidth={241}
        p={2}
        display="flex"
        flexDirection="column"
        style={{ gap: '8px' }}
      >
        {tags.slice(totalVisibleItems).map(tag => (
          <TagLabel
            key={tag.tagName || tag.name}
            {...tag}
            onDelete={
              handleOnDelete &&
              (() => {
                handleOnDelete(tag, false);
              })
            }
            size="small"
          />
        ))}
      </Box>
    </Popover>
  );

  const showMoreTagsButton = useMemo(
    () => (
      <Button
        color="primary"
        variant="contained"
        // @ts-expect-error MUI v4 types incompatible with React 18, will be fixed in MUI v5 migration
        style={{
          display: 'flex',
          borderRadius: '100px',
          maxWidth: '36px',
          minWidth: '36px',
          height: '24px',
          ...(columnView
            ? {}
            : {
                position: 'absolute',
                left: '100%',
              }),
          ...styleBtnPopup,
        }}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(anchorEl ? null : event.currentTarget)
        }
      >
        <MoreHorizIcon />
      </Button>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalVisibleItems, widthVisibleElements]
  );

  useEffect(() => {
    if (!isOverflow) setAnchorEl(null);
  }, [isOverflow]);

  return (
    <Box
      display="flex"
      alignItems="center"
      position="relative"
      gridGap={8}
      minWidth="190px"
    >
      {customAddButton}
      <div
        ref={tagsListRef}
        style={{
          display: 'flex',
          gap: GAP_BETWEEN_TAGS,
          overflow: 'hidden',
          flexFlow: 'wrap',
          maxHeight: heigthOfTagsListDiv || DEFAULT_HEIGTH,
        }}
      >
        {tags.map(tag => (
          <TagLabel
            key={tag.tagName || tag.name}
            {...tag}
            onDelete={handleOnDelete && (() => handleOnDelete(tag, false))}
            size="small"
            ellipsis={ellipsis}
          />
        ))}
      </div>
      {isOverflow && showMoreTagsButton}
      {isOverflow && showMoreTagsPopup}
    </Box>
  );
};

export default RowTagsList;
